import React from "react";
import "./PatternThumb.css";

/*
  VISCAT-386 — static thumbnail of a catalog pattern.

  Draws the same geometry the live grid draws: node i sits at column i % size,
  row floor(i / size) (see getPoints in ../utils), and the pattern is the polyline
  through consecutive path entries (see Connectors).

  Deliberately NOT built on PatternLock. That component registers window-level
  mouseup/touchend/resize listeners, reads layout on every render, runs a rAF loop
  and injects a global stylesheet — machinery a static thumbnail does not need and
  that would be multiplied by every tile in the picker. It also supports exactly one
  marker colour (Point's isFirst), so the three colours below would require editing
  the component that renders the live clinical test grid.

  Three nodes are marked with solid fills, all derived from the pattern itself:
  no fixed start or end node is assumed.
*/
const PatternThumb = ({ pattern, size = 4, className = "" }) => {
  const path = pattern.path;
  const cell = 100 / size;
  const cx = (i) => ((i % size) * cell) + (cell / 2);
  const cy = (i) => (Math.floor(i / size) * cell) + (cell / 2);

  const startNode = path[0];
  const endNode = path[path.length - 1];

  // The one-point mark is the LAST node of the one-point answer: scoring compares
  // answer.slice(0, onePointAnswerPos), so the first `pos` nodes earn the partial
  // point and path[pos - 1] is the node a child must reach to score 1.
  const pos = pattern["one-point-answer-pos"];
  const onePointNode = Number.isInteger(pos) && pos > 0 ? path[pos - 1] : null;

  // Precedence blue > green > red when markers coincide. Start and end are
  // recoverable by eye from the line's own geometry; the one-point mark is not.
  const modifierFor = (i) => {
    if (i === onePointNode) return " pattern-thumb__dot--one-point";
    if (i === startNode) return " pattern-thumb__dot--start";
    if (i === endNode) return " pattern-thumb__dot--end";
    return "";
  };

  const points = path.map((i) => `${cx(i)},${cy(i)}`).join(" ");

  return (
    <svg
      className={`pattern-thumb${className ? ` ${className}` : ""}`}
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
    >
      <polyline className="pattern-thumb__line" points={points} />
      {Array.from({ length: size * size }).map((_, i) => {
        const modifier = modifierFor(i);
        return (
          <circle
            key={i}
            className={`pattern-thumb__dot${modifier}`}
            cx={cx(i)}
            cy={cy(i)}
            r={modifier ? 6 : 3.5}
          />
        );
      })}
    </svg>
  );
};

export default PatternThumb;
